// Considered: PropertyPickerMolecule — rejected as an extension because its contract is
// a PropertyOption[] with lanes excluded by design (it is retired once this ships). The
// aspect catalog is the "add a condition" entry point for all six unified-filter lanes
// (aspect/identity/perspective/relationship/arc/measure), so its rows carry a lane glyph,
// its groups carry an in-scope/out-of-scope badge, and a Recent group leads the list —
// none of which PropertyPickerMolecule's contract expresses. See spec Reuse Audit.
//
// Shares PickerShellMolecule for search/scope-banner/list/group/empty-state chrome — see
// PropertyPickerMolecule.tsx for the same split. This file owns the catalog row markup,
// the group-bucketing rule (by CatalogEntry.group, in insertion order) and the fetch
// states (uf-catalog-states: loading skeleton, error callout with retry).
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'localization';
import PickerShellMolecule, { PickerShellBucket } from '../PickerShell/PickerShellMolecule';
import LaneMarkerAtom from '../../atoms/LaneMarker/LaneMarkerAtom';
import type { FilterLaneKind } from '../../../types/filterLane';
import type { PropertyType } from '../PropertyFilter/PropertyFilterMolecule';
import './AspectCatalogMolecule.scss';

export interface CatalogEntry {
  /** Stable identity within its group — becomes the React key and the row testid suffix. */
  key: string;
  name: string;
  lane: FilterLaneKind;
  /** Short pill shown trailing the row — e.g. the aspect's data type. */
  badge: string;
  /**
   * The aspect's data type (the badge is its label). Decides the operator a picked row
   * opens with (`defaultOperatorFor`): text → contains, numbers → equals, sets → in.
   * Absent on lane entries and on callers that build rows without a type.
   */
  type?: PropertyType;
  /** One-line note under the name — e.g. which sphere type it's defined on. */
  note?: string;
  /**
   * Per-row coverage/scope warning — e.g. "Only on Priority — empty for Strategy".
   * Rendered under the name with a warning glyph, below `note`.
   */
  warning?: string;
  /** De-emphasised — an aspect that exists but is outside the active scope. */
  dim?: boolean;
  /** Group identity; entries without a group render as a flat list. */
  group?: string;
  groupLabel?: string;
  /** `true` = "in scope" badge, `false` = "not in scope" badge, `undefined` = no badge. */
  groupInScope?: boolean;
  /**
   * Parity instrumentation — `data-datum` for this entry's group heading span
   * (read off the group's first entry). A caller embedding this catalog in a
   * different block (e.g. `uf-row`'s in-row property picker) names its own
   * group headings distinctly from the at-rest `uf-catalog` block's.
   */
  groupHeadingDatumId?: string;
  /**
   * Parity instrumentation — per-row `data-datum` overrides. The at-rest
   * `uf-catalog` block numbers rows by *instance* (`instanceSuffix`, the whole
   * catalog rendered a second time); the in-row picker embedded by `uf-row`
   * numbers rows by *position* within one open picker instead (`.button#7`,
   * `.button#8`, …, one pair per row). Omitted → falls back to
   * `${datumScope}.button${instanceSuffix}`/`.glyph${instanceSuffix}`.
   */
  buttonDatumId?: string;
  glyphDatumId?: string;
}

export interface AspectCatalogMoleculeProps {
  /** The full, unfiltered catalog (excludes `recent`). */
  entries: CatalogEntry[];
  /** Shown as a leading "Recent" group when the search box is empty. */
  recent?: CatalogEntry[];
  onPick: (entry: CatalogEntry) => void;
  /**
   * Key of the entry this catalog is currently bound to — rendered selected with a
   * check. Omitted by the add-a-condition callers (nothing is bound yet); set by the
   * callers that RE-pick an already-bound aspect (level sort, Atlas column binding).
   */
  selectedKey?: string | null;
  onCancel?: () => void;
  /**
   * Shows the search field's `Esc` kbd hint (`advanced-filters-v2.jsx:543`) — the "Add a
   * condition" dialog tells the author the popover dismisses on Escape; the at-rest
   * catalog (embedded inline, nothing to dismiss to) omits it. Independent of
   * `onCancel`: a host whose own layer dismisses on Escape (a column popover, a drill-in)
   * shows a true hint without also taking this catalog's Cancel button. Defaults to
   * `false`; every caller that passes it today also passes `onCancel`, so no render moves.
   */
  showEscHint?: boolean;
  /** Sphere-type names currently in scope; empty/undefined renders the no-scope banner. */
  scopeTypeLabels?: string[];
  loading?: boolean;
  /** Non-null renders the failed-to-load callout instead of the list. */
  error?: string | null;
  onRetry?: () => void;
  /** Count of additional sphere types not yet searched — drives the "search more" footer. */
  otherTypeCount?: number;
  onSearchMore?: () => void;
  testIdPrefix?: string;
  className?: string;
  /**
   * Datum-id prefix for this render — the prototype decomposes the same shell into two
   * blocks (`uf-catalog` at rest, `uf-catalog-states` for loading/error/no-results), so the
   * wrapper's `data-datum` must switch with it. Defaults to the at-rest block.
   */
  datumScope?: string;
  /**
   * Appended to every `data-datum` id this render produces. The B2b artboard
   * (`uf-catalog-states`) shows this same card three times side by side to
   * document loading/failed/ready together, so the contract names its second
   * and third occurrences `<id>#2`/`<id>#3` — a `<AspectCatalogTriptych>`
   * story column passes `'#2'`/`'#3'` here; every standalone render omits it.
   */
  instanceSuffix?: string;
  /**
   * When set (and `scopeTypeLabels` is empty), replaces the default "no scope"
   * banner sentence with this caller-worded hint — e.g. the in-row property
   * picker's own "no type in scope yet" copy. Mirrors `EFPropertyPicker`'s
   * `emptyScopeHint` prop (`context/filter-row.jsx:301`); omitted everywhere
   * except that picker.
   */
  emptyScopeHint?: React.ReactNode;
  /** A trailing action appended to `emptyScopeHint` — e.g. "Add a Sphere type filter". */
  emptyScopeAction?: string;
  onEmptyScopeAction?: () => void;
  /** Parity instrumentation — `data-datum` for the `emptyScopeAction` button. */
  emptyScopeActionDatumId?: string;
  /**
   * Parity instrumentation — overrides the bold word inside the default
   * "no scope"/"scoped to N types" banner sentences. Omitted everywhere except
   * a caller (like `uf-row`) whose own decomposition names that word distinctly
   * from `uf-catalog`'s own `.sphere-type`/`.scopetypes-map-eftypename-join`.
   */
  scopeNoneBoldDatumId?: string;
  scopeSomeBoldDatumId?: string;
  /**
   * Parity instrumentation — `data-container` for the wrapper. Defaults to the
   * at-rest `uf-catalog` block's own container (`ui-catalog`); a caller
   * embedding this catalog inside another block's contract (e.g. `uf-row`'s
   * in-row property picker) names its own container instead.
   */
  container?: string;
  /**
   * Total deployed-type count for the search-mode (Tier 2) banner. When set and
   * the search box holds a query, replaces the scope banner with "Searching all
   * N deployed types" — the prototype's `EFPropertyPicker` search mode escapes
   * scope on purpose and says so (`context/filter-row.jsx:267`), matching the
   * same pattern already used by `AdvancedFilterEditorOrganism`'s rail. Omitted
   * (the default) keeps today's always-on scope banner.
   */
  searchTotalTypeCount?: number;
  /** Parity instrumentation — `data-datum` for the search banner's bolded count. */
  searchTotalCountDatumId?: string;
  /**
   * Parity instrumentation — `data-datum` for the search input itself. Omitted
   * everywhere except a caller (like `uf-advanced`) whose own decomposition
   * names the input distinctly from the default's unlabeled search box.
   */
  searchInputDatumId?: string;
  /**
   * Parity instrumentation — overrides the "search N more types" footer button's
   * `data-datum`. Omitted everywhere except a caller whose own decomposition
   * already spends the default `${datumScope}.search` id on a different
   * element (e.g. `uf-advanced`'s own rail search-more button).
   */
  searchMoreDatumId?: string;
  /**
   * Replaces the default "No aspect matches…" empty-search copy. `uf-row`'s
   * in-row property picker words this "No properties match." — distinct from
   * the at-rest catalog's own wording (`context/filter-row.jsx:283` vs.
   * `advanced-filters-v2.jsx:1221`'s shared phrasing, `context/filter-row.jsx:283`).
   * Omitted everywhere except that caller.
   */
  noMatchesMessage?: string;
  /**
   * Replaces the default "Search N more types for a specific aspect…" footer
   * copy. `uf-row`'s in-row property picker words this "for a specific
   * property…" (`context/filter-row.jsx:350`). Omitted everywhere except that
   * caller.
   */
  searchMoreLabel?: string;
  /**
   * Renders in the host's own list instead of a floating shell — no border,
   * shadow, or fixed width; the list clamps to a shorter max-height
   * (`advanced-filters-v2.jsx` `AFAspectCatalog`'s `inline` flag, used by the
   * tree's drill-in "+ Add condition" row, which has nowhere to float a
   * second layer over the one it's already in). Defaults to `false`, the
   * bordered/shadowed popover shell used everywhere else.
   */
  inline?: boolean;
  /**
   * Seeds this catalog's search box on first render. A host that opens the catalog
   * already searched — an artboard documenting the no-results reading, a drill-in
   * carrying the word the author already typed — declares the state instead of
   * driving keystrokes to reach it. Ignored once `query` is supplied (that host owns
   * the value outright). Defaults to `''`, today's empty box.
   */
  initialQuery?: string;
  /**
   * Controlled search query. Supplying it hands the value to the host — the catalog
   * still reports every change through `onQueryChange` and still derives its own
   * search-mode behaviour (Recent drops out, the "search more" footer hides) from it.
   * Omitted (the default), the catalog owns the query as it does today.
   */
  query?: string;
  /** Fires on every search-box change, controlled or not. Omitted by default. */
  onQueryChange?: (query: string) => void;
  /**
   * Column-width reading — forwards `PickerShellMolecule`'s own `dense` and steps the
   * catalog's rows down one notch with it, so an `inline` catalog at ~420px reads as
   * tight as the surface it stands in (`inline` alone drops the border, shadow and
   * width floor but keeps the full-size scale). Independent of `inline`: a host may
   * want either without the other. Defaults to `false`.
   */
  dense?: boolean;
}

// The CONTENT of one option. `PickerShellMolecule` owns the `role="option"` wrapper, its
// id, its `aria-selected` and its activation — an option may hold no interactive
// descendant, so this row is markup only and carries no button, link or handler.
function AspectCatalogRow({
  entry,
  groupLabel,
  selected,
  testIdPrefix,
  datumScope,
  instanceSuffix,
}: Readonly<{
  entry: CatalogEntry;
  groupLabel?: string;
  selected?: boolean;
  testIdPrefix: string;
  datumScope: string;
  instanceSuffix: string;
}>) {
  return (
    <div
      className={`aspect-catalog-molecule__row${entry.dim ? ' aspect-catalog-molecule__row--dim' : ''}${selected ? ' aspect-catalog-molecule__row--selected' : ''}`}
      data-testid={`${testIdPrefix}-${entry.key}`}
      data-datum={entry.buttonDatumId ?? `${datumScope}.button${instanceSuffix}`}
    >
      <span
        className="aspect-catalog-molecule__row-glyph"
        data-datum={entry.glyphDatumId ?? `${datumScope}.glyph${instanceSuffix}`}
        data-kind="glyph"
      >
        <LaneMarkerAtom lane={entry.lane} size="sm" />
      </span>
      <span className="aspect-catalog-molecule__row-main">
        <span className="aspect-catalog-molecule__row-name">{entry.name}</span>
        {(entry.note || groupLabel) && (
          <span className="aspect-catalog-molecule__row-note">{entry.note ?? groupLabel}</span>
        )}
        {entry.warning && (
          <span className="aspect-catalog-molecule__row-warning">
            <i className="pi pi-exclamation-triangle" aria-hidden="true" />
            {entry.warning}
          </span>
        )}
      </span>
      <span className="aspect-catalog-molecule__row-badge">{entry.badge}</span>
      {selected && <i className="pi pi-check aspect-catalog-molecule__row-check" aria-hidden="true" />}
    </div>
  );
}

/** Loading placeholder — skeleton rows, never an empty popover. */
function AspectCatalogSkeleton({ datumScope, instanceSuffix }: Readonly<{ datumScope: string; instanceSuffix: string }>) {
  const widths = [88, 132, 96, 118, 76];
  return (
    <div
      className="aspect-catalog-molecule__skeleton"
      data-testid="aspect-catalog-skeleton"
      data-datum={`${datumScope}.callout${instanceSuffix}`}
      data-kind="callout"
      aria-hidden="true"
    >
      {widths.map((width) => (
        <div key={width} className="aspect-catalog-molecule__skeleton-row">
          <span className="aspect-catalog-molecule__skeleton-glyph" />
          <span className="aspect-catalog-molecule__skeleton-bar" style={{ width }} />
        </div>
      ))}
    </div>
  );
}

const AspectCatalogMolecule: React.FC<AspectCatalogMoleculeProps> = ({
  entries,
  recent = [],
  onPick,
  selectedKey = null,
  onCancel,
  showEscHint = false,
  scopeTypeLabels = [],
  loading = false,
  error = null,
  onRetry,
  otherTypeCount = 0,
  onSearchMore,
  testIdPrefix = 'aspect-catalog-option',
  className = '',
  datumScope = 'uf-catalog',
  instanceSuffix = '',
  emptyScopeHint,
  emptyScopeAction,
  onEmptyScopeAction,
  emptyScopeActionDatumId,
  scopeNoneBoldDatumId,
  scopeSomeBoldDatumId,
  container = 'ui-catalog',
  searchTotalTypeCount,
  searchTotalCountDatumId,
  searchInputDatumId,
  searchMoreDatumId,
  noMatchesMessage,
  searchMoreLabel,
  inline = false,
  initialQuery = '',
  query: controlledQuery,
  onQueryChange,
  dense = false,
}) => {
  const { t } = useTranslation();

  // The prototype hides the "search more" footer while a query is in flight
  // (advanced-filters-v2.jsx:592 gates on `!query`) — controlled so this component
  // can apply that gate instead of leaving it always visible once otherTypeCount>0,
  // and so the Recent group can drop out while searching.
  const [internalQuery, setInternalQuery] = useState(initialQuery);
  const query = controlledQuery ?? internalQuery;
  // Tracked either way: the internal value is what an uncontrolled host renders from,
  // and a controlled host's value simply wins on read.
  const setQuery = useCallback(
    (next: string) => {
      setInternalQuery(next);
      onQueryChange?.(next);
    },
    [onQueryChange]
  );
  const searching = Boolean(query.trim());

  // Recent leads the unfiltered list, exactly like the prototype's `[...recent, ...groups]` —
  // it is a "group" like any other for bucketing purposes. It only leads an empty search:
  // every recent entry is also in `entries`, so keeping it during a query would list each
  // match twice (under Recent and under its own group).
  const items = React.useMemo(
    () =>
      searching
        ? entries
        : [
            ...recent.map((e) => ({ ...e, group: e.group ?? 'recent', groupLabel: e.groupLabel ?? t('filterEditor.catalog.recentGroupLabel') })),
            ...entries,
          ],
    [searching, recent, entries, t]
  );

  const computeBuckets = useCallback(
    (filtered: CatalogEntry[]): PickerShellBucket<CatalogEntry>[] | null => {
      if (!filtered.some((e) => e.group)) return null;
      const order: string[] = [];
      const byGroup = new Map<string, CatalogEntry[]>();
      filtered.forEach((e) => {
        const groupKey = e.group ?? '';
        if (!byGroup.has(groupKey)) {
          order.push(groupKey);
          byGroup.set(groupKey, []);
        }
        byGroup.get(groupKey)?.push(e);
      });
      return order.map((groupKey) => {
        const groupItems = byGroup.get(groupKey) ?? [];
        const first = groupItems[0];
        let badge: React.ReactNode;
        if (first.groupInScope === true) {
          badge = <span className="aspect-catalog-molecule__group-badge aspect-catalog-molecule__group-badge--in">{t('filterEditor.catalog.inScope')}</span>;
        } else if (first.groupInScope === false) {
          badge = <span className="aspect-catalog-molecule__group-badge aspect-catalog-molecule__group-badge--out">{t('filterEditor.catalog.notInScope')}</span>;
        }
        return {
          key: groupKey,
          label: first.groupLabel ?? groupKey,
          badge,
          labelDatum: first.groupHeadingDatumId,
          items: groupItems,
        };
      });
    },
    [t]
  );

  const wrapperClassName = `aspect-catalog-molecule${dense ? ' aspect-catalog-molecule--dense' : ''} ${className}`.trim();

  // Split so the dynamic/bold segment (the in-scope type names, or the literal
  // "sphere type" nudge) can carry its own parity datum — the prototype bolds it
  // inline within one sentence (advanced-filters-v2.jsx:549/550), not as a
  // standalone string.
  // Which scope sentence applies, in precedence order: an in-flight search over
  // every type, an explicit type scope, a caller-supplied empty-scope hint, or
  // the generic "no sphere type" nudge.
  const searchingAll = searchTotalTypeCount !== undefined && searching;
  let scopeBanner: React.ReactNode;
  let scopeLabel: string;
  if (searchingAll) {
    scopeBanner = (
      <span className="picker-shell-molecule__scope-label">
        {t('filterEditor.catalog.searchingAllPrefix')}{' '}
        <strong data-datum={searchTotalCountDatumId ?? `${datumScope}.ef-org-type-count${instanceSuffix}`}>
          {searchTotalTypeCount}
        </strong>{' '}
        {t('filterEditor.catalog.searchingAllSuffix', { count: searchTotalTypeCount })}
      </span>
    );
    scopeLabel = t('filterEditor.catalog.searchingAllSuffix', { count: searchTotalTypeCount });
  } else if (scopeTypeLabels.length) {
    scopeBanner = (
      <span className="picker-shell-molecule__scope-label">
        {t('filterEditor.catalog.scopeSomePrefix')}{' '}
        <strong
          className="aspect-catalog-molecule__scope-emphasis"
          data-datum={scopeSomeBoldDatumId ?? `${datumScope}.scopetypes-map-eftypename-join${instanceSuffix}`}
        >
          {scopeTypeLabels.join(' · ')}
        </strong>{' '}
        {t('filterEditor.catalog.scopeSomeSuffix')}
      </span>
    );
    scopeLabel = t('filterEditor.catalog.scopeSome', { types: scopeTypeLabels.join(' · ') });
  } else if (emptyScopeHint) {
    scopeBanner = (
      <span className="picker-shell-molecule__scope-label">
        {emptyScopeHint}
        {emptyScopeAction && onEmptyScopeAction && (
          <>
            {' '}
            <button
              type="button"
              className="aspect-catalog-molecule__scope-action"
              onClick={onEmptyScopeAction}
              data-datum={emptyScopeActionDatumId}
              data-kind={emptyScopeActionDatumId ? 'button' : undefined}
            >
              {emptyScopeAction}
            </button>
          </>
        )}
      </span>
    );
    scopeLabel = t('filterEditor.catalog.scopeNone');
  } else {
    scopeBanner = (
      <span className="picker-shell-molecule__scope-label">
        {t('filterEditor.catalog.scopeNonePrefix')}{' '}
        <strong data-datum={scopeNoneBoldDatumId ?? `${datumScope}.sphere-type${instanceSuffix}`}>
          {t('filterEditor.catalog.scopeNoneBold')}
        </strong>{' '}
        {t('filterEditor.catalog.scopeNoneSuffix')}
      </span>
    );
    scopeLabel = t('filterEditor.catalog.scopeNone');
  }

  return (
    // A named fieldset (a group), not a landmark: the catalog is a transient picker inside
    // a row, popover or pane, not a page region a person navigates to. As a named
    // `<section>` (a region landmark) any two open at once — rows side by side, a popover
    // over a pane — shared one name (axe `landmark-unique`).
    <fieldset
      className={wrapperClassName}
      data-testid={loading ? `${testIdPrefix}-loading` : undefined}
      data-datum={`${datumScope}.card${instanceSuffix}`}
      data-kind="card"
      data-container={container}
      aria-label={t('filterEditor.catalog.dialogLabel')}
    >
      <PickerShellMolecule<CatalogEntry>
        className={inline ? 'picker-shell-molecule--inline' : undefined}
        dense={dense}
        items={items}
        getSearchText={(e) => e.name}
        getRowKey={(e) => `${e.group ?? ''}:${e.key}`}
        renderRow={(e, { grouped }) => (
          <AspectCatalogRow
            entry={e}
            groupLabel={grouped ? undefined : e.groupLabel}
            selected={selectedKey != null && e.key === selectedKey}
            testIdPrefix={testIdPrefix}
            datumScope={datumScope}
            instanceSuffix={instanceSuffix}
          />
        )}
        onPickItem={onPick}
        isItemSelected={(e) => selectedKey != null && e.key === selectedKey}
        computeBuckets={computeBuckets}
        onCancel={onCancel}
        scopeLabel={scopeLabel}
        scopeBanner={scopeBanner}
        alwaysShowScope
        query={query}
        onQueryChange={setQuery}
        loading={loading}
        renderLoading={() => <AspectCatalogSkeleton datumScope={datumScope} instanceSuffix={instanceSuffix} />}
        errorTitle={error ? t('filterEditor.catalog.loadFailedTitle') : undefined}
        errorBody={error ? t('filterEditor.catalog.loadFailedBody') : undefined}
        errorDatum={`${datumScope}.couldn-t-load-the-aspect-catalog${instanceSuffix}`}
        // The copy names what was searched (`No aspect matches “{{query}}”`), so the
        // query has to travel with it — without it the placeholder reaches the screen.
        noMatchesMessage={noMatchesMessage ?? t('filterEditor.catalog.noMatches', { query })}
        searchPlaceholder={t('filterEditor.panel.searchPlaceholder')}
        cancelLabel={t('common.actions.cancel')}
        searchIconDatum={`${datumScope}.icon${instanceSuffix}`}
        searchInputDatum={searchInputDatumId ?? `${datumScope}.search-aspects${instanceSuffix}`}
        escHint={
          showEscHint ? (
            <kbd className="aspect-catalog-molecule__esc-hint">{t('filterEditor.catalog.escHint')}</kbd>
          ) : undefined
        }
        testIdPrefix={testIdPrefix}
      />
      {error && onRetry && (
        <button type="button" className="aspect-catalog-molecule__retry" onClick={onRetry} data-testid={`${testIdPrefix}-retry`}>
          {t('filterEditor.panel.retry')}
        </button>
      )}
      {!error && !loading && !searching && otherTypeCount > 0 && onSearchMore && (
        <button
          type="button"
          className="aspect-catalog-molecule__search-more"
          onClick={onSearchMore}
          data-testid={`${testIdPrefix}-search-more`}
          data-datum={searchMoreDatumId ?? `${datumScope}.search${instanceSuffix}`}
        >
          <i className="pi pi-search" aria-hidden="true" />
          {searchMoreLabel ?? t('filterEditor.catalog.searchMore', { count: otherTypeCount })}
        </button>
      )}
    </fieldset>
  );
};

AspectCatalogMolecule.displayName = 'AspectCatalogMolecule';

export default AspectCatalogMolecule;
